import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Chip,
  Card,
  Typography,
  Box,
  Dialog,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  TextField,
  IconButton,
  useTheme,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FlakyOutlinedIcon from '@mui/icons-material/FlakyOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DialogBoxComponent } from '../../components/DialogBox/DialogBoxCard';
import EHApprovalStatus from './EHApprovalStatus';
import KeycloakService from '../../services/KeycloakService';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import {
  UpdateExternalHrsRequestByFinance,
  UpdateExternalHrsRequestBySeniorManager,
  updateApplicationByUser,
} from '../../services/ExternalHrsService';
import { useDispatch } from 'react-redux';
import { SHOW_NOTIFICATION } from '../../constants';
import ApplyExternalHours from './ApplyExternalHours';
import { getHolidays } from '../../admin/Services/MasterdataService';
import { getUserData } from '../../services/UserService';
import { countWorkingDays, toHolidaySet, extractHolidayRows } from './externalHoursWorkingDays';
import moment from 'moment';

const getStatusMessage = (applicationStatus, requestCategory, HRApprovedHrs) => {
  if (HRApprovedHrs) {
    return 'Approved Hours:';
  }
  switch (applicationStatus) {
    case 'REJECTED':
      return 'Rejected';
    case 'CANCELLED':
      return 'Cancelled by Employee';
    case 'DISCARDED':
      return 'Application Discarded';
    case 'APPROVED':
      return 'Approved';
    case 'SENIOR_MANAGER_APPROVED':
      return 'Pending Approval from Finance';
    case 'MANAGER_APPROVED':
      return 'Pending Approval from Finance (Legacy)';
    case 'PENDING':
      return requestCategory === 'CLIENT_OFFICE'
        ? 'Pending Approval from Senior Manager'
        : 'Pending Approval from Finance';
    default:
      return applicationStatus || 'Pending';
  }
};

const getDisplayStatusLabel = (applicationStatus) => {
  switch (applicationStatus) {
    case 'SENIOR_MANAGER_APPROVED':
      return 'Senior Manager Approved';
    case 'MANAGER_APPROVED':
      return 'Manager Approved (Legacy)';
    default:
      return applicationStatus;
  }
};

const formatIsoDate = (isoDate) => {
  if (!isoDate) return '';
  const parsed = moment(
    typeof isoDate === 'string' ? isoDate.slice(0, 10) : isoDate,
    'YYYY-MM-DD',
    typeof isoDate === 'string',
  );
  if (!parsed.isValid()) return String(isoDate);
  return parsed.format('DD MMM YYYY');
};

const ExternalHoursRequestCard = (props) => {
  const {
    requestId,
    employeeName,
    employeeCode,
    employeeId: applicantEmployeeId,
    monthRange,
    startDate,
    endDate,
    requestedHrs,
    reason,
    applicationStatus,
    HRApprovedHrs,
    ApprovalData,
    financePage,
    seniorManagerPage,
    userPage,
    setRefresh,
    requestCategory,
  } = props;

  const [holidaySet, setHolidaySet] = useState(new Set());

  const isExternalHours = requestCategory !== 'CLIENT_OFFICE';
  const hasDateRange = Boolean(startDate && endDate);
  const hoursTypeLabel =
    requestCategory === 'CLIENT_OFFICE'
      ? 'Client Office Hours'
      : requestCategory === 'WORK_TRAVEL'
        ? 'Work Travel Hours'
        : 'External Hours';
  const hoursMetricLabel = isExternalHours && hasDateRange
    ? 'Requested Hours/Day:'
    : monthRange?.[0] === monthRange?.[2] && monthRange?.[1] === monthRange?.[3]
      ? 'Requested Hours:'
      : 'Requested Hours/Month:';
  const approveHoursLabel =
    isExternalHours && hasDateRange ? 'Approve Hours/Day' : 'Approve Hours/Month';

  useEffect(() => {
    if (!isExternalHours || !hasDateRange) return undefined;
    let cancelled = false;
    const loadHolidays = async () => {
      try {
        const targetUserId =
          applicantEmployeeId || KeycloakService.getUserDetail();
        const user = await getUserData(targetUserId);
        const locationId = user?.data?.locationId;
        if (!locationId) return;
        const holidayList = await getHolidays(locationId);
        if (cancelled) return;
        setHolidaySet(toHolidaySet(extractHolidayRows(holidayList)));
      } catch (e) {
        // Weekends still excluded if holidays fail to load.
      }
    };
    loadHolidays();
    return () => {
      cancelled = true;
    };
  }, [isExternalHours, hasDateRange, applicantEmployeeId]);

  const workingDays = useMemo(() => {
    if (!isExternalHours || !hasDateRange) return 0;
    return countWorkingDays(startDate, endDate, holidaySet);
  }, [isExternalHours, hasDateRange, startDate, endDate, holidaySet]);

  const approveHoursMin = 1;
  const approveHoursMax =
    isExternalHours && hasDateRange ? 12 : 200;
  const cumulativeRequested =
    isExternalHours && hasDateRange ? workingDays * Number(requestedHrs || 0) : null;
  const cumulativeApproved =
    isExternalHours && hasDateRange && HRApprovedHrs
      ? workingDays * Number(HRApprovedHrs || 0)
      : null;

  const dateRangeMetricFields =
    isExternalHours && hasDateRange ? (
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label="Working Days"
          value={workingDays}
          disabled
          InputProps={{ readOnly: true }}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Cumulative Hours"
          value={cumulativeRequested ?? 0}
          disabled
          InputProps={{ readOnly: true }}
          sx={{ width: 140 }}
        />
        {cumulativeApproved != null && (
          <TextField
            size="small"
            label="Cumulative Approved"
            value={cumulativeApproved}
            disabled
            InputProps={{ readOnly: true }}
            sx={{ width: 150 }}
          />
        )}
      </Stack>
    ) : null;

  const currentUserId = KeycloakService.getUserDetail();
  const isFinanceApprover = !!financePage && KeycloakService.hasRole(['finance']);
  const isSeniorManagerApprover =
    !!seniorManagerPage && KeycloakService.hasRole(['senior-manager']);

  const canFinanceAct =
    isFinanceApprover &&
    ((requestCategory !== 'CLIENT_OFFICE' &&
      (applicationStatus === 'PENDING' ||
        applicationStatus === 'MANAGER_APPROVED')) ||
      (requestCategory === 'CLIENT_OFFICE' &&
        (applicationStatus === 'SENIOR_MANAGER_APPROVED' ||
          applicationStatus === 'MANAGER_APPROVED')));

  const canSeniorManagerAct =
    isSeniorManagerApprover &&
    requestCategory === 'CLIENT_OFFICE' &&
    applicationStatus === 'PENDING';

  const canApproveOrReject = canFinanceAct || canSeniorManagerAct;

  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = React.useState();
  const [confirmText, setConfirmText] = React.useState();
  const [comment, setComment] = React.useState(null);

  const [userEditedHours, setUserEditedHours] = useState('');
  const [userEditedReason, setUserEditedReason] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const toggleDialogClose = () => {
    setOpenDialog(false);
  };
  const toggleOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  const approvedHrsDefault = HRApprovedHrs || requestedHrs;
  const disableEditHrs =
    applicationStatus === 'APPROVED' || applicationStatus === 'CANCELLED';

  const [approvedHrs, setApprovedHrs] = useState(approvedHrsDefault);

  const handleClose = () => {
    setComment(null);
    setOpen(false);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const dispatch = useDispatch();

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const buildApprovalBody = (status) => ({
    id: requestId,
    status,
    approvedHours: status === 'REJECTED' ? null : approvedHrs,
    comment: comment,
  });

  const handleReject = async () => {
    const body = buildApprovalBody('REJECTED');
    const response = isSeniorManagerApprover
      ? await UpdateExternalHrsRequestBySeniorManager(body)
      : await UpdateExternalHrsRequestByFinance(body);
    setOpen(false);
    setRefresh((prev) => !prev);
    dipatchNotification(response.type, response.message);
  };

  const handleApprove = async () => {
    const status = isSeniorManagerApprover
      ? 'SENIOR_MANAGER_APPROVED'
      : 'APPROVED';
    const body = buildApprovalBody(status);
    const response = isSeniorManagerApprover
      ? await UpdateExternalHrsRequestBySeniorManager(body)
      : await UpdateExternalHrsRequestByFinance(body);
    setOpen(false);
    setRefresh((prev) => !prev);
    dipatchNotification(response.type, response.message);
  };

  const handleCancelApplication = async () => {
    const body = {
      id: requestId,
      employeeId: currentUserId,
      status: 'CANCELLED',
      tenantId: process.env.REACT_APP_TENANT_ID,
    };

    const response = await updateApplicationByUser(body);
    setOpen(false);
    setRefresh((prev) => !prev);
    dipatchNotification(response.type, response.message);
  };

  const getFormattedMonthRange = (range) => {
    if (!Array.isArray(range) || range.length < 4) return '';
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const startMonth = monthNames[range[0] - 1];
    const startYear = range[1];
    const endMonth = monthNames[range[2] - 1];
    const endYear = range[3];

    if (startMonth === endMonth && startYear === endYear) {
      return `${startMonth} ${startYear}`;
    } else if (startYear === endYear) {
      return `${startMonth} - ${endMonth} ${startYear}`;
    } else {
      return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    }
  };

  const getFormattedRangeLabel = () => {
    if (hasDateRange) {
      const startLabel = formatIsoDate(startDate);
      const endLabel = formatIsoDate(endDate);
      return startLabel === endLabel
        ? startLabel
        : `${startLabel} - ${endLabel}`;
    }
    return getFormattedMonthRange(monthRange);
  };

  const cancelApplication = async () => {
    handleCancelApplication();
  };

  const handleOnConfirm = () => {
    if (confirmText === 'Approve') handleApprove();
    else handleReject();
  };

  const handleUserEditClick = async () => {
    setUserEditedHours(requestedHrs);
    setUserEditedReason(reason);
    toggleOpenDialog();
  };

  const canCancel =
    applicationStatus === 'PENDING' || applicationStatus === 'MANAGER_APPROVED';
  const canEdit =
    applicationStatus === 'PENDING' || applicationStatus === 'DISCARDED';

  const statusMessage = getStatusMessage(
    applicationStatus,
    requestCategory,
    HRApprovedHrs,
  );

  const theme = useTheme();
  return (
    <Box>
      <DialogBoxComponent
        open={open}
        handleClose={handleClose}
        confirmText={confirmText}
        cancelText={'Close'}
        content={dialogContent}
        onConfirm={
          confirmText == 'Confirm' ? cancelApplication : handleOnConfirm
        }
        onCancel={handleClose}
        component={
          confirmText == 'Confirm' ? null : (
            <TextField
              sx={{ marginLeft: '10px', marginRight: '10px' }}
              id="outlined-multiline-static"
              label="Comment"
              multiline
              rows={4}
              value={comment}
              onChange={handleCommentChange}
            />
          )
        }
        icon={<FlakyOutlinedIcon color="primary" />}
      />
      <Card
        variant="outlined"
        sx={{ mb: 2, p: 2, px: { xs: 0, sm: 2 }, pb: 0 }}
      >
        <Box>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
              minHeight: userPage && isExternalHours && hasDateRange ? 90 : 'auto',
            }}
          >
            {!userPage && (
              <Typography variant="h6" width={'340px'}>
                {employeeName + ` (${employeeCode})`}
              </Typography>
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent={canApproveOrReject ? 'left' : 'none'}
              width={'340px'}
              flexWrap="nowrap"
            >
              <CalendarMonthRoundedIcon
                sx={{ fontSize: 'x-large', flexShrink: 0 }}
              />
              <Tooltip title={getFormattedRangeLabel()} arrow>
                <Typography
                  sx={{
                    fontSize: '1.1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getFormattedRangeLabel()}
                </Typography>
              </Tooltip>
              <Chip
                size="small"
                label={hoursTypeLabel}
                sx={{ ml: 1, flexShrink: 0 }}
              />
            </Stack>
            {userPage && (
              <Box
                className="details-container"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  transform: 'translateX(-50%)',
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      border: `1px solid #bdbdbd`,
                      borderRadius: '1.5rem',
                      px: '.5rem',
                      py: '.2rem',
                      justifySelf: 'center',
                      mb: { xs: '.5rem', sm: 0 },
                      alignContent: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.8rem', color: '#663300' }}
                    >
                      {hoursMetricLabel}
                    </Typography>
                    <Avatar
                      sx={{
                        bgcolor: '#b35900',
                        color: 'white',
                        width: 20,
                        height: 20,
                        fontSize: '.7rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {requestedHrs}
                    </Avatar>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      border:
                        applicationStatus === 'DISCARDED' ||
                        applicationStatus === 'REJECTED'
                          ? `1px solid ${theme.palette.error.main}`
                          : `1px solid #bdbdbd`,
                      borderRadius: '1.5rem',
                      py: '.2rem',
                      px: '.5rem',
                      justifyItems: 'center',
                      alignContent: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '.8rem',
                        color: HRApprovedHrs
                          ? theme.palette.success.main
                          : '#663300',
                      }}
                    >
                      {statusMessage}
                    </Typography>
                    {HRApprovedHrs && (
                      <Avatar
                        sx={{
                          bgcolor: 'success.main',
                          color: 'white',
                          width: 20,
                          height: 20,
                          fontSize: '.7rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {HRApprovedHrs || 0}
                      </Avatar>
                    )}
                  </Stack>
                </Box>
                {dateRangeMetricFields}
              </Box>
            )}
            <Box
              width={'165px'}
              display="flex"
              justifyContent="center"
              alignSelf="flex-start"
              pt={userPage && isExternalHours && hasDateRange ? 0.5 : 0}
            >
              <Chip
                label={getDisplayStatusLabel(applicationStatus)}
                color={
                  applicationStatus === 'APPROVED'
                    ? 'success'
                    : applicationStatus === 'REJECTED' ||
                        applicationStatus === 'DISCARDED'
                      ? 'error'
                      : 'warning'
                }
                variant="outlined"
              />
            </Box>
          </Box>
          {!userPage && (
            <Box
              className="details-container"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mt: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  border: `1px solid #bdbdbd`,
                  borderRadius: '1.5rem',
                  px: '.5rem',
                  py: '.2rem',
                  justifySelf: 'center',
                  mb: { xs: '.5rem', sm: 0 },
                  alignContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', color: '#663300' }}
                >
                  {hoursMetricLabel}
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: '#b35900',
                    color: 'white',
                    width: 20,
                    height: 20,
                    fontSize: '.7rem',
                    fontWeight: 'bold',
                  }}
                >
                  {requestedHrs}
                </Avatar>
              </Stack>
              {dateRangeMetricFields}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  border: `1px solid #bdbdbd`,
                  borderRadius: '1.5rem',
                  py: '.2rem',
                  px: '.5rem',
                  justifyItems: 'center',
                  alignContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '.8rem',
                    color: HRApprovedHrs
                      ? theme.palette.success.main
                      : '#663300',
                  }}
                >
                  {statusMessage}
                </Typography>
                {HRApprovedHrs && (
                  <Avatar
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      width: 20,
                      height: 20,
                      fontSize: '.7rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {HRApprovedHrs || 0}
                  </Avatar>
                )}
              </Stack>
            </Box>
          )}
          <Box
            className="details-container"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: canApproveOrReject ? 3 : 2,
            }}
          >
            {userPage && (
              <>
                <Button
                  disabled={!canCancel}
                  variant="outlined"
                  color="error"
                  sx={{ fontSize: '12px' }}
                  onClick={() => {
                    setOpen(true);
                    setConfirmText('Confirm');
                    setDialogContent(
                      'Are you sure, you want to cancel this application?',
                    );
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!canEdit}
                  variant="outlined"
                  color="success"
                  sx={{ fontSize: '12px' }}
                  onClick={() => {
                    handleUserEditClick();
                  }}
                >
                  Edit
                </Button>
                <Dialog
                  open={openDialog}
                  onClose={() => toggleDialogClose()}
                  fullWidth
                  maxWidth="md"
                >
                  <ApplyExternalHours
                    toggleOpen={toggleDialogClose}
                    setRefresh={setRefresh}
                    hours={userEditedHours}
                    reason={userEditedReason}
                    month={monthRange}
                    startDate={startDate}
                    endDate={endDate}
                    requestId={requestId}
                    lockedCategory={requestCategory || 'EXTERNAL'}
                    initialRequestCategory={requestCategory || 'EXTERNAL'}
                  />
                </Dialog>
              </>
            )}
            {canApproveOrReject && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ fontSize: '12px', maxHeight: '40px' }}
                  onClick={() => {
                    setOpen(true);
                    setConfirmText('Reject');
                    setDialogContent(
                      'Are you sure, you want to Reject this application?',
                    );
                  }}
                >
                  Reject
                </Button>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    label={approveHoursLabel}
                    type="text"
                    error={
                      approvedHrs > approveHoursMax ||
                      approvedHrs < approveHoursMin
                    }
                    helperText={
                      approvedHrs > approveHoursMax ||
                      approvedHrs < approveHoursMin
                        ? `*provide value b/w ${approveHoursMin} and ${approveHoursMax}`
                        : ''
                    }
                    variant="outlined"
                    size="small"
                    value={Number(approvedHrs)}
                    disabled={disableEditHrs}
                    sx={{
                      width: '140px',
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                        {
                          display: 'none',
                        },
                      '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                      },
                      '& .MuiOutlinedInput-root': {
                        display: 'flex',
                        alignItems: 'center',
                        '& fieldset': {
                          borderColor: disableEditHrs ? '#707070' : '',
                        },
                        '& input': {
                          textAlign: 'center',
                        },
                      },
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (inputValue === '' || inputValue === '0') {
                        setApprovedHrs('0');
                      } else {
                        const cleanedValue = inputValue.replace(/^0+/, '');
                        setApprovedHrs(Number(cleanedValue) || 0);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={() => {
                              setApprovedHrs(parseInt(approvedHrs, 10) - 1);
                            }}
                            aria-label="decrement"
                            size="small"
                            disabled={
                              disableEditHrs || approvedHrs <= approveHoursMin
                            }
                          >
                            <RemoveIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setApprovedHrs(parseInt(approvedHrs, 10) + 1);
                            }}
                            aria-label="increment"
                            size="small"
                            disabled={
                              disableEditHrs || approvedHrs >= approveHoursMax
                            }
                          >
                            <AddIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <Button
                  disabled={
                    !(
                      typeof approvedHrs == 'number' &&
                      approvedHrs <= approveHoursMax &&
                      approvedHrs >= approveHoursMin
                    )
                  }
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ fontSize: '12px', maxHeight: '40px' }}
                  onClick={() => {
                    setOpen(true);
                    setConfirmText('Approve');
                    setDialogContent(
                      `Are you sure, you want to Approve this application?`,
                    );
                  }}
                >
                  Approve
                </Button>
              </>
            )}
          </Box>
        </Box>
        {HRApprovedHrs && applicationStatus === 'APPROVED' && (
          <Typography
            sx={{
              marginTop: 2,
              width: '100%',
              textAlign: 'center',
              fontSize: '12px',
            }}
          >
            <InfoOutlinedIcon color="info" sx={{ fontSize: '13px' }} />{' '}
            {`Application approved by Finance`}
          </Typography>
        )}

        {applicationStatus === 'DISCARDED' && (
          <Typography
            sx={{
              marginTop: 2,
              width: '100%',
              textAlign: 'center',
              fontSize: '12px',
            }}
          >
            <InfoOutlinedIcon color="info" sx={{ fontSize: '13px' }} />
            {`Application discarded due to project discontinuation`}
          </Typography>
        )}
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            boxShadow: 'none',
            '&::before': {
              position: 'relative',
            },
          }}
        >
          <Grid
            className="wfh-container pb-0 pt-0"
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} />
          </Grid>
          <Grid className="wfh-container pb-0 pt-0" sx={{ width: '100%' }}>
            <AccordionDetails>
              <Tooltip title={reason || ''} arrow>
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                  }}
                >
                  {reason}
                </Typography>
              </Tooltip>
              <Typography
                variant="body1"
                className="mt-1 mb-1"
                color={'#4D6CD9'}
              >
                <strong>Approval Status:</strong>
              </Typography>
              {(ApprovalData || []).map((approver) => {
                return (
                  <EHApprovalStatus
                    key={approver.id || `${approver.approverId}-${approver.status}`}
                    approverName={approver.approverName}
                    approverComments={approver.comment}
                    updatedOn={approver.dateOfResponse}
                    approvalStatus={approver.status}
                    approvalStage={approver.approvalStage}
                    approverRole={approver.approverRole}
                  />
                );
              })}
              {applicationStatus === 'APPROVED' &&
                HRApprovedHrs != null &&
                (ApprovalData || []).length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      p: 1,
                      backgroundColor: '#eafaf1',
                      border: '1px solid',
                      borderColor: 'success.light',
                      borderRadius: 1,
                    }}
                  >
                    {isExternalHours && hasDateRange
                      ? `The request for ${requestedHrs} hours per working day was approved as ${HRApprovedHrs} hours per working day, and hence the cumulative quota is ${cumulativeApproved} hours.`
                      : `The request for ${requestedHrs} hours was approved as ${HRApprovedHrs} hours.`}
                  </Typography>
                )}
            </AccordionDetails>
          </Grid>
        </Accordion>
      </Card>
    </Box>
  );
};

export default ExternalHoursRequestCard;
